import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    FiCheckCircle, FiPackage, FiClock, FiArrowLeft, FiZap, FiCreditCard,
    FiSmartphone, FiTruck, FiCopy,
} from 'react-icons/fi';
import {
    getProdutosDisponiveisApi,
    getProfileApi,
    createPixPaymentApi,
    createCardPaymentApi,
    getPaymentStatusByIdApi,
    verifyPaymentsMercadoPagoApi,
    cancelPixPaymentApi,
} from '../../Api';
import { notify } from '../../components/Notify';
import { getUENUser, isAuthenticatedUEN } from '../../utils';
import CartaoForm from '../../components/CartaoForm';
import FreteCalculator from '../../components/FreteCalculator';
import DadosCarteirinhaForm from '../../components/DadosCarteirinhaForm';
import PagamentoRecusadoModal from '../../components/PagamentoRecusadoModal';
import HorarioFuncionamento from '../../components/HorarioFuncionamento';
import imgCardDigital from '../../assets/produtos/card-digital.svg';
import imgCardBundle from '../../assets/produtos/card-bundle.svg';
import imgCardUpgradeFisica from '../../assets/produtos/card-upgrade-fisica.svg'

// Mapeia o combo de flags do produto pra imagem do card.
// Os arquivos em src/assets/produtos/ são placeholders SVG — basta trocar
// o conteúdo (mantendo o nome do arquivo) ou substituir pela versão final
// em PNG/JPG ajustando o import acima.
//
//   digital=1, fisica=0, frete=0  -> imagem A (só digital)
//   digital=1, fisica=1, frete=1  -> imagem B (bundle completo)
//   digital=0, fisica=1, frete=1  -> imagem C (upgrade física + frete)
//   qualquer outra combinação     -> fallback (imagem A)
function getProductImage(produto) {
    // frete é derivado de física — a imagem depende só de digital/física.
    const d = produto.digital === 1;
    const f = produto.fisica === 1;
    if (d && !f) return imgCardDigital;
    if (d && f) return imgCardBundle;
    if (!d && f) return imgCardUpgradeFisica;
    return imgCardDigital;
}

// Vitrine de produtos pós-login. Aceita 3 estados vindos do backend:
//   COMPRAR_NOVA           -> 2 SKUs (só digital, bundle completo)
//   COMPRAR_UPGRADE_FISICA -> 1 SKU (física+frete, sem digital)
//   NADA                   -> nada pra comprar nesse ano
//
// O checkout não tem rota própria — é uma view interna do mesmo componente
// (state machine: list -> checkout -> pix_waiting). Evita URL com productId
// que poderia ser manipulada pelo usuário pra disparar cobrança fora do filtro.
const VIEW = {
    LIST: 'list',
    CHECKOUT: 'checkout',
    PIX_WAITING: 'pix_waiting',
};

export default function Produtos() {
    const navigate = useNavigate();
    const location = useLocation();

    const [loading, setLoading] = useState(true);
    const [proximaAcao, setProximaAcao] = useState(null);
    const [produtos, setProdutos] = useState([]);
    const [userEmail, setUserEmail] = useState('');
    const [view, setView] = useState(VIEW.LIST);
    const [selected, setSelected] = useState(null);
    // Modal de "cartão recusado" — abre vindo do cadastro (location.state) ou
    // quando o checkout daqui recusa um cartão.
    const [recusaModal, setRecusaModal] = useState(
        location.state?.cardRecusado
            ? { open: true, motivo: location.state.motivo || '' }
            : { open: false, motivo: '' }
    );

    // Limpa o state da navegação pra o modal não reabrir em refresh/re-render.
    useEffect(() => {
        if (location.state?.cardRecusado) {
            navigate(location.pathname, { replace: true, state: {} });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!isAuthenticatedUEN()) {
            navigate('/login', { replace: true });
            return;
        }

        const controller = new AbortController();
        (async () => {
            try {
                const [resProdutos, resProfile] = await Promise.all([
                    getProdutosDisponiveisApi({ signal: controller.signal }),
                    getProfileApi({ signal: controller.signal }),
                ]);
                setProximaAcao(resProdutos.data.proxima_acao);
                setProdutos(resProdutos.data.produtos || []);
                setUserEmail(resProfile.data.email_contato || resProfile.data.usuario || '');
            } catch (err) {
                if (controller.signal.aborted) return;
                console.error('Erro ao carregar produtos:', err);
                notify('Não foi possível carregar a lista de produtos.', 'error');
            } finally {
                if (!controller.signal.aborted) setLoading(false);
            }
        })();

        return () => controller.abort();
    }, [navigate]);

    const startCheckout = (produto) => {
        setSelected(produto);
        setView(VIEW.CHECKOUT);
    };

    const backToList = () => {
        setSelected(null);
        setView(VIEW.LIST);
    };

    if (loading) {
        return <div style={styles.loading}>Carregando produtos...</div>;
    }

    if (proximaAcao === 'NADA') {
        return (
            <div style={styles.container}>
                <div style={styles.emptyCard}>
                    <FiCheckCircle size={56} color="#10B981" style={{ marginBottom: 16 }} />
                    <h2 style={styles.emptyTitle}>Tudo certo pra esse ano!</h2>
                    <p style={styles.emptyMessage}>
                        Você já tem a carteirinha digital e a versão física.
                    </p>
                    <p style={styles.emptyMessage}>
                        Não há nada a mais para comprar nesse período.
                    </p>
                    <button style={styles.primaryBtn} onClick={() => navigate('/minhacarteirinha')}>
                        Ir para minha carteirinha
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            {view === VIEW.LIST && (
                <ListaProdutos
                    proximaAcao={proximaAcao}
                    produtos={produtos}
                    onBuy={startCheckout}
                />
            )}

            {view === VIEW.CHECKOUT && selected && (
                <Checkout
                    produto={selected}
                    userEmail={userEmail}
                    onBack={backToList}
                    onPixGerado={(pixData) => {
                        setSelected({ ...selected, pixData });
                        setView(VIEW.PIX_WAITING);
                    }}
                    onCardAprovado={() => navigate('/minhacarteirinha')}
                    onCardRecusado={(motivo) => { backToList(); setRecusaModal({ open: true, motivo }); }}
                />
            )}

            {view === VIEW.PIX_WAITING && selected?.pixData && (
                <PixWaiting
                    pixData={selected.pixData}
                    onAprovado={() => navigate('/minhacarteirinha')}
                    onExpirou={backToList}
                    onCancelar={backToList}
                />
            )}

            {recusaModal.open && (
                <PagamentoRecusadoModal
                    motivo={recusaModal.motivo}
                    onClose={() => setRecusaModal({ open: false, motivo: '' })}
                />
            )}
        </div>
    );
}

// ---------- Lista ----------

function ListaProdutos({ proximaAcao, produtos, onBuy }) {
    const navigate = useNavigate();
    const isUpgrade = proximaAcao === 'COMPRAR_UPGRADE_FISICA';

    // Sem produtos retornados pelo backend — pode ser:
    //   - COMPRAR_NOVA vazio: catálogo do ano ainda não cadastrado / todos inativos
    //   - COMPRAR_UPGRADE_FISICA vazio: SKU de upgrade não cadastrado pro ano
    // Em ambos os casos o usuário não tem nada que possa fazer; mostramos um
    // estado adequado em vez do grid vazio.
    if (produtos.length === 0) {
        if (isUpgrade) {
            return (
                <div style={styles.emptyCard}>
                    <FiPackage size={56} color="#10B981" style={{ marginBottom: 16 }} />
                    <h2 style={styles.emptyTitle}>Upgrade indisponível no momento</h2>
                    <p style={styles.emptyMessage}>
                        A versão física com frete ainda não está sendo oferecida para esse ano.
                        Sua carteirinha digital continua válida e pode ser usada normalmente —
                        a gente avisa quando o upgrade liberar.
                    </p>
                    <button style={styles.primaryBtn} onClick={() => navigate('/minhacarteirinha')}>
                        Voltar para minha carteirinha
                    </button>
                </div>
            );
        }

        return (
            <div style={styles.emptyCard}>
                <FiClock size={56} color="#94A3B8" style={{ marginBottom: 16 }} />
                <h2 style={styles.emptyTitle}>Nenhuma carteirinha à venda agora</h2>
                <p style={styles.emptyMessage}>
                    Estamos atualizando o catálogo do ano. Volte em alguns minutos
                    — ou nos chame no WhatsApp se precisar de ajuda.
                </p>
                <button style={styles.primaryBtn} onClick={() => navigate('/')}>
                    Voltar ao início
                </button>
            </div>
        );
    }

    return (
        <>
            <header style={styles.header}>
                <div style={styles.titleRow}>
                    <h1 style={styles.title}>
                        {isUpgrade ? 'Adicione a versão física' : 'Escolha sua carteirinha'}
                    </h1>
                    <HorarioFuncionamento variant="chip" />
                </div>
                <p style={styles.subtitle}>
                    {isUpgrade
                        ? 'Você já tem a digital. Complemente com a carteirinha em PVC entregue na sua casa.'
                        : 'Pague no Pix para receber na hora ou no cartão em até 12x.'}
                </p>
            </header>

            <div style={styles.grid}>
                {produtos.map((p) => (
                    <ProductCard key={p.id} produto={p} onBuy={() => onBuy(p)} />
                ))}
            </div>
        </>
    );
}

function ProductCard({ produto, onBuy }) {
    const flags = useMemo(() => {
        const list = [];
        if (produto.digital === 1) list.push({ Icon: FiSmartphone, label: 'Carteirinha digital' });
        if (produto.fisica === 1) list.push({ Icon: FiCreditCard, label: 'Carteirinha física (PVC)' });
        if (produto.fisica === 1) list.push({ Icon: FiTruck, label: 'Entrega em casa' });
        return list;
    }, [produto]);

    const imagem = getProductImage(produto);

    return (
        <div style={styles.card}>
            <div style={styles.cardImageWrapper}>
                <img src={imagem} alt={produto.title} style={styles.cardImage} />
            </div>

            <div style={styles.cardBody}>
                <div>
                    <div style={styles.cardBadge}>{produto.card_type || 'Carteirinha'}</div>
                    <h2 style={styles.cardTitle}>{produto.title}</h2>
                    {produto.description && (
                        <p style={styles.cardDescription}>{produto.description}</p>
                    )}

                    <ul style={styles.featureList}>
                        {flags.map((f) => {
                            const Icon = f.Icon;
                            return (
                                <li key={f.label} style={styles.featureItem}>
                                    <span style={styles.featureIcon}><Icon size={16} /></span> {f.label}
                                </li>
                            );
                        })}
                    </ul>
                </div>

                <div style={styles.cardFooter}>
                    <div>
                        <span style={styles.cardPriceLabel}>Valor</span>
                        <div style={styles.cardPrice}>
                            R$ {Number(produto.price).toFixed(2)}
                        </div>
                    </div>
                    <button style={styles.primaryBtn} onClick={onBuy}>
                        Comprar
                    </button>
                </div>
            </div>
        </div>
    );
}

// ---------- Checkout ----------

function Checkout({ produto, userEmail, onBack, onPixGerado, onCardAprovado, onCardRecusado }) {
    const user = getUENUser();
    const [method, setMethod] = useState('pix');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    // Quando o back bloqueia por já existir um Pix em aberto (de OUTRO produto),
    // guardamos o id pra oferecer o botão de cancelar aqui mesmo.
    const [pendingPixId, setPendingPixId] = useState(null);
    const [cancelandoPendente, setCancelandoPendente] = useState(false);

    // Captura o bloqueio "uma compra por vez" (409) vindo do back.
    const tratarErroCompra = (err, fallback) => {
        const data = err.response?.data;
        if (data?.pendingPix && data?.pendingPixId) {
            setPendingPixId(String(data.pendingPixId));
        }
        const msg = data?.error || data?.details || err.message || fallback;
        setError(msg);
        notify(msg, 'error');
    };

    const handleCancelarPendente = async () => {
        if (cancelandoPendente || !pendingPixId) return;
        setCancelandoPendente(true);
        try {
            await cancelPixPaymentApi({ paymentId: pendingPixId });
            setPendingPixId(null);
            setError(null);
            notify('Pagamento em aberto cancelado. Pode comprar agora.', 'success');
        } catch {
            notify('Não foi possível cancelar o pagamento em aberto.', 'error');
        } finally {
            setCancelandoPendente(false);
        }
    };

    // Frete: pra produtos com frete=1 o cliente precisa confirmar o CEP/valor
    // ANTES de ver Pix/Cartão. fretePronto guarda { cepDestino, freteValor }
    // após o cliente confirmar no FreteCalculator.
    const [fretePronto, setFretePronto] = useState(null);
    // Dados da carteirinha (instituição/curso/escolaridade/foto) coletados antes de pagar.
    const [dadosCarteirinha, setDadosCarteirinha] = useState(null);
    const exigeFrete = produto.fisica === 1; // física implica frete (calculado nos Correios)

    // Etapas do checkout: dados → (endereço/frete se física) → pagamento.
    // Upgrade (produto sem digital) NÃO pede dados de novo — o aluno já tem a digital.
    const precisaDados = produto.digital === 1;
    const dadosProntos = !precisaDados || !!dadosCarteirinha;
    const enderecoPronto = !exigeFrete || !!fretePronto;
    const liberaPagamento = dadosProntos && enderecoPronto;

    const valorProduto = Number(produto.price) || 0;
    const valorTotal = valorProduto + (fretePronto?.freteValor || 0);

    // Pacote de dados aplicado na carteirinha quando o pagamento aprova.
    const montarDadosCadastro = () => ({
        ...(dadosCarteirinha || {}),
        ...(fretePronto?.endereco ? { endereco: fretePronto.endereco } : {}),
    });

    const handleGerarPix = async () => {
        if (loading) return;
        setError(null);
        setLoading(true);
        try {
            const res = await createPixPaymentApi({
                productId: produto.id,
                userId: user?.id,
                cepDestino: fretePronto?.cepDestino,
                codigoServico: fretePronto?.codigoServico,
                dadosCadastro: montarDadosCadastro(),
            });
            onPixGerado(res.data);
        } catch (err) {
            tratarErroCompra(err, 'Erro ao gerar Pix.');
        } finally {
            setLoading(false);
        }
    };

    // Secure Fields — o CartaoForm tokeniza no navegador; recebemos token + bandeira.
    const handleCardSubmit = async ({ token, paymentMethodId, issuerId, installments, paymentTypeId, billingAddress, phone }) => {
        setError(null);
        setLoading(true);
        try {
            const deviceId = typeof window !== 'undefined' ? window.MP_DEVICE_SESSION_ID : undefined;
            const res = await createCardPaymentApi({
                productId: produto.id,
                token,
                paymentMethodId,
                issuerId,
                installments,
                paymentTypeId,
                deviceId,
                billingAddress,
                phone,
                cepDestino: fretePronto?.cepDestino,
                codigoServico: fretePronto?.codigoServico,
                dadosCadastro: montarDadosCadastro(),
            });

            if (res.data?.approved) {
                notify('Pagamento aprovado!', 'success');
                onCardAprovado();
                return;
            }

            // Concatena error + details pra exibir a causa real quando o backend
            // não conseguiu traduzir a mensagem.
            const errorMsg = res.data?.error || 'Pagamento recusado.';
            const detailsMsg = res.data?.details;
            const fullMsg = detailsMsg && detailsMsg !== errorMsg
                ? `${errorMsg} ${detailsMsg}`
                : errorMsg;
            throw new Error(fullMsg);
        } catch (err) {
            // Bloqueio "uma compra por vez" (Pix em aberto) → guarda pra oferecer cancelar.
            if (err.response?.data?.pendingPix && err.response?.data?.pendingPixId) {
                setPendingPixId(String(err.response.data.pendingPixId));
            }
            const backendErr = err.response?.data?.error;
            const backendDetails = err.response?.data?.details;
            const msg = backendErr && backendDetails && backendDetails !== backendErr
                ? `${backendErr} ${backendDetails}`
                : (backendErr || backendDetails || err.message || 'Erro ao processar o pagamento.');
            // Cartão recusado (high_risk, saldo, etc.): troca pro Pix e oferece
            // o botão na hora. O Pix não passa pelo antifraude do MP, então é
            // o caminho confiável. (No caso de Pix já em aberto, NÃO troca —
            // o fluxo de cancelar o pendente cuida disso.)
            if (err.response?.data?.pendingPix) {
                // Pix em aberto: mantém aqui pro fluxo de cancelar o pendente.
                setError(msg);
                notify(msg, 'error');
            } else {
                // Cartão recusado: volta pra lista e abre o modal recomendando Pix.
                onCardRecusado?.(msg);
            }
            // Re-lança pro CartaoForm: o Brick rejeita, re-habilita e RE-TOKENIZA
            // no próximo "Pagar". Token de cartão é de USO ÚNICO — sem isso, um
            // retry reenviaria o token já consumido → invalid_card_token.
            throw err;
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <button onClick={onBack} style={styles.backBtn} disabled={loading}>
                <FiArrowLeft size={16} /> Voltar
            </button>

            <div style={styles.checkoutResumo}>
                <div>
                    <span style={styles.cardBadge}>{produto.card_type || 'Carteirinha'}</span>
                    <h2 style={styles.checkoutTitle}>{produto.title}</h2>
                </div>
                <div style={styles.cardPrice}>R$ {valorTotal.toFixed(2)}</div>
            </div>

            {/* Etapa 1: dados da carteirinha (só pra produto com digital) */}
            {precisaDados && !dadosCarteirinha && (
                <DadosCarteirinhaForm
                    loading={loading}
                    onConfirmar={(d) => { setDadosCarteirinha(d); setError(null); }}
                />
            )}

            {precisaDados && dadosCarteirinha && (
                <div style={styles.etapaOk}>
                    ✓ Dados da carteirinha preenchidos
                    <button type="button" onClick={() => setDadosCarteirinha(null)} style={styles.editarLink} disabled={loading}>
                        Editar
                    </button>
                </div>
            )}

            {/* Etapa 2 (física): endereço + frete */}
            {dadosProntos && exigeFrete && (
                <FreteCalculator
                    produtoNome={produto.title}
                    produtoValor={valorProduto}
                    loading={loading}
                    onConfirmar={(dados) => { setFretePronto(dados); setError(null); }}
                    onResetar={() => setFretePronto(null)}
                />
            )}

            {/* Etapa 3: pagamento */}
            {liberaPagamento && (
                <>
                    <h3 style={styles.sectionTitle}>Forma de pagamento</h3>
                    <div style={styles.methodTabs}>
                        {[
                            { id: 'pix', label: 'Pix', Icon: FiZap },
                            { id: 'card', label: 'Cartão', Icon: FiCreditCard },
                        ].map((m) => {
                            const active = method === m.id;
                            const Icon = m.Icon;
                            return (
                                <button
                                    key={m.id}
                                    type="button"
                                    disabled={loading}
                                    onClick={() => { setMethod(m.id); setError(null); }}
                                    style={{
                                        ...styles.methodTab,
                                        ...(active ? styles.methodTabActive : {}),
                                    }}
                                >
                                    <Icon size={22} />
                                    <span>{m.label}</span>
                                </button>
                            );
                        })}
                    </div>

                    {method === 'pix' && (
                        <div style={styles.pixBlock}>
                            <p style={styles.helperText}>
                                Você gera o QR Code e tem 15 minutos pra pagar. A carteirinha é liberada
                                automaticamente assim que confirmamos o pagamento.
                            </p>
                            <button
                                onClick={handleGerarPix}
                                disabled={loading}
                                style={{
                                    ...styles.primaryBtn,
                                    background: loading
                                        ? '#94A3B8'
                                        : 'linear-gradient(135deg, #059669 0%, #10B981 100%)',
                                    boxShadow: loading ? 'none' : '0 6px 16px -4px rgba(16, 185, 129, 0.4)',
                                    width: '100%',
                                    marginTop: '12px',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
                                }}
                            >
                                <FiZap size={18} />
                                {loading ? 'Gerando Pix...' : `Pagar R$ ${valorTotal.toFixed(2)} no Pix`}
                            </button>
                        </div>
                    )}

                    {method === 'card' && (
                        <div style={styles.brickWrapper}>
                            <CartaoForm
                                valor={valorTotal}
                                paymentTypeId="credit_card"
                                loading={loading}
                                onSubmit={handleCardSubmit}
                                maxInstallments={12}
                                userPhone={user?.telefone}
                                payerEmail={userEmail}
                            />
                        </div>
                    )}
                </>
            )}

            {error && (
                <div style={styles.errorBox}>
                    <strong>Pagamento não realizado.</strong>
                    <div style={{ marginTop: '4px' }}>{error}</div>
                    {pendingPixId && (
                        <button
                            onClick={handleCancelarPendente}
                            disabled={cancelandoPendente}
                            style={{
                                ...styles.secondaryBtn,
                                marginTop: '10px',
                                color: '#9F1239',
                                borderColor: '#FECDD3',
                                opacity: cancelandoPendente ? 0.6 : 1,
                            }}
                        >
                            {cancelandoPendente ? 'Cancelando...' : 'Cancelar pagamento em aberto'}
                        </button>
                    )}
                </div>
            )}
        </>
    );
}

// ---------- Pix waiting (QR + polling) ----------

function PixWaiting({ pixData, onAprovado, onExpirou, onCancelar }) {
    const [timeLeft, setTimeLeft] = useState('');
    const [isChecking, setIsChecking] = useState(false);
    const [isCancelling, setIsCancelling] = useState(false);
    const stoppedRef = useRef(false);

    const expiresAt = pixData?.expires_at;
    const paymentId = pixData?.paymentId;

    // Contador regressivo.
    useEffect(() => {
        if (!expiresAt) return;
        const tick = () => {
            const distance = new Date(expiresAt).getTime() - Date.now();
            if (distance < 0) {
                setTimeLeft('EXPIRADO');
                onExpirou();
                return false;
            }
            const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((distance % (1000 * 60)) / 1000);
            setTimeLeft(`${String(minutes).padStart(2, '0')}m ${String(seconds).padStart(2, '0')}s`);
            return true;
        };
        if (!tick()) return;
        const interval = setInterval(() => {
            if (!tick()) clearInterval(interval);
        }, 1000);
        return () => clearInterval(interval);
    }, [expiresAt, onExpirou]);

    // Polling de status (a cada 10s) — sai assim que aprovar.
    useEffect(() => {
        if (!paymentId) return;
        stoppedRef.current = false;
        let timeoutId;

        const poll = async () => {
            if (stoppedRef.current) return;
            try {
                const res = await getPaymentStatusByIdApi({ paymentId });
                if (stoppedRef.current) return;
                if (res.data?.status === 'approved') {
                    onAprovado();
                    return;
                }
            } catch {
                /* segue tentando */
            }
            if (!stoppedRef.current) timeoutId = setTimeout(poll, 10000);
        };

        timeoutId = setTimeout(poll, 10000);
        return () => {
            stoppedRef.current = true;
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [paymentId, onAprovado]);

    const handleJaPaguei = async () => {
        if (isChecking) return;
        setIsChecking(true);
        try {
            const res = await verifyPaymentsMercadoPagoApi({ transactionId: paymentId });
            if (res.data?.approved) {
                onAprovado();
                return;
            }
            notify('Pagamento ainda não confirmado.', 'info');
        } catch {
            notify('Erro na consulta. Tente novamente em instantes.', 'error');
        } finally {
            setIsChecking(false);
        }
    };

    const handleCopy = () => {
        if (!pixData.pixCopiaECola) return;
        navigator.clipboard.writeText(pixData.pixCopiaECola);
        notify('Código Pix copiado!', 'success');
    };

    const formatExpiration = (isoString) => {
        if (!isoString) return '';
        return new Date(isoString).toLocaleTimeString('pt-BR', {
            timeZone: 'America/Sao_Paulo', hour: '2-digit', minute: '2-digit', hour12: false,
        });
    };

    const handleCancelar = async () => {
        if (isCancelling) return;
        setIsCancelling(true);
        stoppedRef.current = true; // para o polling
        try {
            const res = await cancelPixPaymentApi({ paymentId });
            // Edge: o Pix já tinha sido pago no intervalo — o back provisiona e avisa.
            if (res.data?.approved) {
                notify('Esse Pix já tinha sido pago! Sua carteirinha foi liberada.', 'success');
                onAprovado();
                return;
            }
            notify('Pagamento cancelado. Você pode escolher outro produto.', 'info');
            onCancelar();
        } catch {
            notify('Não foi possível cancelar agora. Tente de novo.', 'error');
            stoppedRef.current = false; // retoma o polling se falhou
        } finally {
            setIsCancelling(false);
        }
    };

    return (
        <div style={pixStyles.wrap}>
            <h2 style={pixStyles.title}>Pague o Pix</h2>
            <p style={pixStyles.helper}>Escaneie o QR Code ou copie o código abaixo.</p>

            <div style={pixStyles.timerBox}>
                <p style={pixStyles.expira}>
                    Expira às <span style={pixStyles.expiraVal}>{formatExpiration(pixData?.expires_at)}</span>
                </p>
                <p style={pixStyles.countdown}>
                    <FiClock size={18} /> {timeLeft === 'EXPIRADO' ? 'Código expirado' : timeLeft}
                </p>
            </div>

            {pixData.pixQrCodeBase64 && (
                <div style={pixStyles.qrBox}>
                    <img
                        src={`data:image/jpeg;base64,${pixData.pixQrCodeBase64}`}
                        alt="QR Code Pix"
                        style={pixStyles.qrImg}
                    />
                </div>
            )}

            <textarea
                readOnly
                value={pixData.pixCopiaECola || ''}
                rows={3}
                style={pixStyles.textarea}
                onClick={(e) => e.target.select()}
            />

            <button onClick={handleCopy} style={pixStyles.btnSecondary}>
                <FiCopy size={16} /> Copiar código Pix
            </button>

            <button
                onClick={handleJaPaguei}
                disabled={isChecking || isCancelling}
                style={{ ...pixStyles.btnPrimary, opacity: (isChecking || isCancelling) ? 0.6 : 1 }}
            >
                {isChecking ? 'Verificando…' : 'Já paguei'}
            </button>

            <button
                onClick={handleCancelar}
                disabled={isCancelling || isChecking}
                style={{ ...pixStyles.btnCancel, opacity: (isCancelling || isChecking) ? 0.6 : 1 }}
            >
                {isCancelling ? 'Cancelando…' : 'Cancelar e escolher outro'}
            </button>
        </div>
    );
}

// Estilo canônico do Pix (mesmo de Step4Payment e ModalPix).
const pixStyles = {
    wrap: {
        width: '100%', maxWidth: 420, margin: '0 auto', background: '#FFFFFF',
        border: '1px solid #E2E8F0', borderRadius: 16, padding: '1.5rem',
        textAlign: 'center', fontFamily: "'Inter','Roboto',system-ui,sans-serif",
    },
    title: { fontSize: '1.25rem', fontWeight: 800, color: '#0F172A', margin: '0 0 4px' },
    helper: { fontSize: '0.88rem', color: '#64748B', margin: '0 0 1rem' },
    timerBox: {
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
        margin: '0 0 1rem', padding: '0.6rem 1rem',
        background: '#F8FAFC', borderRadius: 10, border: '1px solid #E2E8F0',
    },
    expira: { fontSize: '0.78rem', color: '#64748B', margin: 0 },
    expiraVal: { fontWeight: 700, color: '#0F172A' },
    countdown: {
        fontSize: '1.1rem', fontWeight: 700, color: '#E11D48', margin: 0,
        display: 'flex', alignItems: 'center', gap: 6,
    },
    qrBox: {
        background: '#fff', padding: 12, borderRadius: 12, border: '1px solid #E2E8F0',
        display: 'inline-block', boxShadow: '0 4px 12px -2px rgba(15,23,42,0.06)',
    },
    qrImg: { width: 200, height: 200, display: 'block' },
    textarea: {
        width: '100%', marginTop: '1rem', padding: '0.65rem', borderRadius: 10,
        border: '1px solid #E2E8F0', fontSize: '0.8rem', resize: 'none',
        fontFamily: 'inherit', color: '#334155', boxSizing: 'border-box',
    },
    btnPrimary: {
        width: '100%', marginTop: '0.6rem', padding: '0.8rem', borderRadius: 10,
        border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg,#059669,#10B981)',
        color: '#fff', fontWeight: 700, fontSize: '0.9rem',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontFamily: 'inherit',
    },
    btnSecondary: {
        width: '100%', marginTop: '0.6rem', padding: '0.8rem', borderRadius: 10,
        border: '1px solid #CBD5E1', background: '#fff', color: '#475569',
        fontWeight: 600, fontSize: '0.88rem', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontFamily: 'inherit',
    },
    btnCancel: {
        width: '100%', marginTop: '0.5rem', padding: '0.8rem', borderRadius: 10,
        border: '1px solid #FECDD3', background: '#fff', color: '#9F1239',
        fontWeight: 600, fontSize: '0.88rem', cursor: 'pointer', fontFamily: 'inherit',
    },
};

// ---------- Estilos ----------

const PRIMARY = '#059669';   // Indigo 600
const ACCENT  = '#10B981';   // Indigo 500
const TEXT    = '#0F172A';   // Slate 900
const MUTED   = '#64748B';   // Slate 500
const BG      = '#F8FAFC';   // Slate 50

const styles = {
    loading: { padding: '40px', textAlign: 'center', color: MUTED },
    container: {
        padding: '0',
        maxWidth: '1024px',
        margin: '0 auto',
        fontFamily: "'Inter', 'Roboto', system-ui, sans-serif",
        color: TEXT,
    },
    etapaOk: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
        background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: 10,
        padding: '0.6rem 0.85rem', marginBottom: '1rem',
        fontSize: '0.85rem', fontWeight: 600, color: '#047857',
    },
    editarLink: {
        background: 'transparent', border: 'none', color: '#047857',
        fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer', textDecoration: 'underline', fontFamily: 'inherit',
    },
    header: { marginBottom: '28px' },
    titleRow: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 8 },
    title: { color: TEXT, fontSize: '1.875rem', fontWeight: 700, margin: 0, letterSpacing: '-0.02em' },
    subtitle: { color: MUTED, fontSize: '0.95rem', margin: 0 },

    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, 300px)',
        gap: '20px',
        justifyContent: 'center',
    },
    card: {
        width: 300,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderRadius: '16px',
        backgroundColor: '#fff',
        border: '1px solid #E2E8F0',
        boxShadow: '0 1px 3px 0 rgba(15, 23, 42, 0.08), 0 1px 2px -1px rgba(15, 23, 42, 0.06)',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    },
    cardImageWrapper: {
        width: '100%',
        aspectRatio: '16 / 9',
        background: 'linear-gradient(135deg, #ECFDF5 0%, #ECFDF5 100%)',
        overflow: 'hidden',
    },
    cardImage: {
        width: '100%',
        height: '100%',
        display: 'block',
        objectFit: 'cover',
    },
    cardBody: {
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        gap: '24px',
        flex: 1,
    },
    cardBadge: {
        display: 'inline-block',
        padding: '4px 10px',
        background: `${PRIMARY}15`,
        color: PRIMARY,
        fontSize: '11px',
        fontWeight: 700,
        borderRadius: '999px',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        marginBottom: '8px',
    },
    cardTitle: { color: TEXT, fontSize: '1.125rem', fontWeight: 700, margin: '0 0 8px' },
    cardDescription: { color: MUTED, fontSize: '0.875rem', lineHeight: 1.5, margin: '0 0 16px' },

    featureList: { listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '8px' },
    featureItem: { display: 'flex', alignItems: 'center', gap: '8px', color: '#334155', fontSize: '0.875rem' },
    featureIcon: { color: '#10B981', display: 'grid', placeItems: 'center' },

    cardFooter: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '16px',
        paddingTop: '16px',
        borderTop: '1px solid #F1F5F9',
    },
    cardPriceLabel: { color: '#94A3B8', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.05em' },
    cardPrice: { color: TEXT, fontSize: '1.5rem', fontWeight: 800, letterSpacing: '-0.01em' },

    primaryBtn: {
        padding: '0.7rem 1.25rem',
        background: 'linear-gradient(135deg, #059669 0%, #10B981 100%)',
        color: '#fff',
        border: 'none',
        borderRadius: '10px',
        fontWeight: 600,
        fontSize: '0.9rem',
        cursor: 'pointer',
        boxShadow: '0 4px 12px -2px rgba(5, 150, 105, 0.4)',
        fontFamily: 'inherit',
        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
    },
    secondaryBtn: {
        padding: '0.65rem 1.25rem',
        backgroundColor: 'transparent',
        color: '#475569',
        border: '1.5px solid #E2E8F0',
        borderRadius: '10px',
        fontWeight: 600,
        fontSize: '0.875rem',
        cursor: 'pointer',
        width: '100%',
        fontFamily: 'inherit',
    },
    backBtn: {
        background: 'none',
        border: 'none',
        color: MUTED,
        cursor: 'pointer',
        fontSize: '0.875rem',
        fontWeight: 500,
        padding: '0.4rem 0.6rem',
        marginBottom: '16px',
        borderRadius: 8,
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.4rem',
        fontFamily: 'inherit',
    },

    checkoutResumo: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '16px',
        padding: '1.25rem 1.5rem',
        borderRadius: '14px',
        background: 'linear-gradient(135deg, #ECFDF5 0%, #ECFDF5 100%)',
        border: '1px solid #D1FAE5',
        marginBottom: '24px',
    },
    checkoutTitle: { color: TEXT, fontSize: '1.25rem', fontWeight: 700, margin: 0, letterSpacing: '-0.01em' },

    sectionTitle: { color: TEXT, fontSize: '0.95rem', fontWeight: 700, margin: '0 0 12px' },
    methodTabs: { display: 'flex', gap: '0.75rem', marginBottom: '20px', flexWrap: 'wrap' },
    methodTab: {
        flex: '1 1 140px',
        padding: '0.95rem 0.75rem',
        borderRadius: '12px',
        border: '1.5px solid #E2E8F0',
        backgroundColor: '#fff',
        color: '#475569',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '0.4rem',
        cursor: 'pointer',
        fontWeight: 600,
        fontSize: '0.875rem',
        fontFamily: 'inherit',
        transition: 'all 0.15s ease',
    },
    methodTabActive: {
        borderColor: PRIMARY,
        backgroundColor: '#ECFDF5',
        color: '#047857',
        boxShadow: '0 0 0 3px rgba(5, 150, 105, 0.1)',
    },

    pixBlock: { padding: '1.25rem', borderRadius: '12px', backgroundColor: BG, border: '1px solid #E2E8F0' },
    helperText: { color: MUTED, fontSize: '0.875rem', lineHeight: 1.5, margin: 0 },

    brickWrapper: { padding: '1.25rem', borderRadius: '12px', backgroundColor: BG, border: '1px solid #E2E8F0' },

    errorBox: {
        marginTop: '12px',
        padding: '0.75rem 1rem',
        borderRadius: '10px',
        backgroundColor: '#FFF1F2',
        color: '#9F1239',
        border: '1px solid #FECDD3',
        fontSize: '0.875rem',
    },

    pixWaitingCard: {
        padding: '2rem',
        borderRadius: '16px',
        backgroundColor: '#fff',
        border: '1px solid #E2E8F0',
        boxShadow: '0 4px 16px -8px rgba(15, 23, 42, 0.08)',
        maxWidth: '500px',
        margin: '0 auto',
        textAlign: 'center',
    },
    pixTimer: {
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '0.2rem',
        margin: '1rem 0',
        padding: '0.6rem 1.5rem',
        borderRadius: '999px',
        backgroundColor: '#FFF1F2',
        color: '#9F1239',
        border: '1px solid #FECDD3',
    },
    pixTimerLabel: { fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700 },
    pixTimerValue: { fontSize: '1.25rem', fontWeight: 800, letterSpacing: '-0.01em' },
    qrWrapper: {
        display: 'inline-block',
        padding: '12px',
        backgroundColor: '#fff',
        borderRadius: '12px',
        border: '1px solid #E2E8F0',
        boxShadow: '0 4px 12px -2px rgba(15, 23, 42, 0.06)',
    },
    qrImage: { width: '220px', height: '220px', display: 'block' },
    copyArea: {
        width: '100%',
        marginTop: '1rem',
        marginBottom: '0.5rem',
        padding: '0.6rem',
        border: '1px solid #E2E8F0',
        borderRadius: '8px',
        fontSize: '0.75rem',
        color: '#475569',
        resize: 'none',
        fontFamily: "'JetBrains Mono', monospace",
        boxSizing: 'border-box',
        backgroundColor: BG,
    },

    emptyCard: {
        textAlign: 'center',
        padding: '3rem 1.5rem',
        backgroundColor: '#fff',
        borderRadius: '16px',
        border: '1px solid #E2E8F0',
        maxWidth: '500px',
        margin: '40px auto',
        boxShadow: '0 1px 3px 0 rgba(15, 23, 42, 0.08)',
    },
    emptyIcon: { fontSize: '48px', display: 'block', marginBottom: '16px' },
    emptyTitle: { color: TEXT, fontSize: '1.5rem', fontWeight: 700, margin: '0 0 12px', letterSpacing: '-0.01em' },
    emptyMessage: { color: MUTED, fontSize: '0.95rem', lineHeight: 1.6, margin: '0 0 24px' },
};
